/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { Box, CircularProgress, Link, Stack, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

import { useWindowDimensions } from "../../utils/Hooks";
import { updateCompany } from "./companyFunctions";
import { Company, Nullable, ServiceLocation } from "../../utils/Interfaces";
import { GridRenderCellParams } from "@mui/x-data-grid";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import DataTable from "../../components/table/DataTable";
import CreateSiteModal from "../office-work-sites/CreateSiteModal";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

const CompanyDetails = () => {
    const { height } = useWindowDimensions();
    const { companyId } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState<Nullable<Company>>(null);
    const [sites, setSites] = useState<ServiceLocation[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const columns = [
        { field: "id", headerName: "id", filterable: false, sortable: true },
        {
            field: "Edit",
            headerName: "Edit",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                const onClickEdit = (e: React.MouseEvent) => {
                    // don't select this row after clicking
                    e.stopPropagation();
                    setIsModalOpen(true);
                };

                return (
                    <Tooltip title="Edit Site">
                        <IconButton onClick={onClickEdit} size="large">
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "store_no",
            width: 150,
            headerName: "Store number",
            sortable: true,
            filterable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/work-sites/${params.row.id}`)}>
                        <Typography variant="overline" fontSize={14}>
                            {params.row.store_no}
                        </Typography>
                    </Link>
                );
            },
        },
        {
            field: "street_address",
            width: 450,
            headerName: "street address",
            sortable: true,
            filterable: true,
        },
        {
            width: 250,
            field: "city",
            headerName: "city",
            sortable: true,
            filterable: true,
        },
        {
            field: "state",
            headerName: "state",
            sortable: true,
            filterable: true,
        },
        {
            field: "zip_code",
            headerName: "Zip Code",
            sortable: true,
            filterable: true,
        },
    ];

    useEffect(() => {
        updateCompany(parseInt(companyId as string), dispatch, setCompany, setSites, setLoading);
    }, []);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    { text: "Companies", route: "/companies" },
                    company ? { text: company.name, route: `/companies/${company.id}` } : null,
                ]}
            />

            {loading && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 20 }}>
                    <CircularProgress size={40} />
                </Stack>
            )}

            {!loading && (
                <Box>
                    <Typography variant="h6">{company?.name}</Typography>
                    <Box style={{ paddingTop: "10px" }}>
                        <DataTable
                            tableName="COMPANY_DETAILS"
                            height={height - 220}
                            columns={columns}
                            data={sites}
                            allowRowSelected={false}
                        >
                            <Button onClick={() => setIsModalOpen(true)}>Add New Site</Button>
                        </DataTable>
                    </Box>

                    {isModalOpen && (
                        <CreateSiteModal
                            open={isModalOpen}
                            handleClose={() => setIsModalOpen(false)}
                            defaultCompany={company as Company}
                            onSiteCreated={() =>
                                updateCompany(parseInt(companyId as string), dispatch, setCompany, setSites, setLoading)
                            }
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CompanyDetails;
